import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navigation } from "@/components/Navigation";
import { PublicFooter } from "@/components/PublicFooter";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { getTenantSubdomain, isTenantRootDomain } from "@/lib/tenant";
import { getBrandConfig } from "@/config/brand";
import { APP_HOME_ROUTE, getPostAuthRoute } from "@/lib/routes";
import { useTenant } from "@/hooks/useTenant";
import { SUPPORT_EMAIL } from "@/lib/constants";
import { localizeApiErrorDetail } from "@/lib/apiError";
import { lowercaseEmailSchema } from "@/lib/emailSchema";

type LoginFormValues = {
  email: string;
  password: string;
};

const Login = () => {
  const { t } = useTranslation();
  const brand = getBrandConfig();
  const { login, isLoading } = useAuth();
  const { data: tenant } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const handledSsoError = useRef<string | null>(null);
  const handledRecoveryReason = useRef<string | null>(null);
  const handledBootstrapToken = useRef<string | null>(null);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const rootDomain = isTenantRootDomain(window.location.hostname);
  const tenantSubdomain = getTenantSubdomain(window.location.hostname);

  const loginSchema = useMemo(
    () =>
      z.object({
        email: lowercaseEmailSchema(t("auth.invalidEmail"), t("auth.lowercaseEmail")),
        password: z.string().min(1, t("auth.passwordRequired")),
      }),
    [t],
  );
  const ssoErrorMessages = useMemo<Record<string, string>>(
    () => ({
      SSO_NOT_CONFIGURED: t("auth.ssoNotConfigured"),
      TENANT_REQUIRED: t("auth.tenantRequired"),
      SSO_DOMAIN_NOT_ALLOWED: t("auth.ssoDomainNotAllowed"),
      SSO_EMAIL_CONFLICT: t("auth.ssoEmailConflict"),
      SSO_CALLBACK_FAILED: t("auth.ssoCallbackFailed"),
      SSO_ACCOUNT_NOT_REGISTERED: t("auth.ssoAccountNotRegistered"),
    }),
    [t],
  );

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = (data: LoginFormValues) => {
    login(data);
  };

  const bootstrapToken = searchParams.get("token");

  useEffect(() => {
    if (!bootstrapToken || handledBootstrapToken.current === bootstrapToken) return;
    handledBootstrapToken.current = bootstrapToken;

    const clearTokenFromUrl = () => {
      const next = new URLSearchParams(window.location.search);
      next.delete("token");
      setSearchParams(next, { replace: true });
    };

    const bootstrap = async () => {
      try {
        const response = await api.post("/api/v1/auth/session", null, {
          headers: { Authorization: `Bearer ${bootstrapToken}` },
        });
        setAuth(response.data, null);
        toast.success(t("auth.welcomeWorkspace"));
        clearTokenFromUrl();
        navigate(getPostAuthRoute(window.location.hostname));
      } catch (error: any) {
        clearTokenFromUrl();
        toast.error(t("auth.signupFailed"), {
          description:
            localizeApiErrorDetail(error.response?.data?.detail, t, t("auth.signupFailedDescription")) ??
            t("auth.signupFailedDescription"),
        });
      }
    };

    bootstrap();
  }, [bootstrapToken, navigate, setAuth, setSearchParams, t]);

  useEffect(() => {
    const code = searchParams.get("sso_error");
    if (!code || handledSsoError.current === code) return;
    handledSsoError.current = code;
    toast.error(t("auth.ssoFailed"), {
      description: ssoErrorMessages[code] || t("auth.ssoFailedDescription"),
    });
  }, [searchParams, ssoErrorMessages, t]);

  useEffect(() => {
    const reason = searchParams.get("reason");
    if (!reason || handledRecoveryReason.current === reason) return;
    handledRecoveryReason.current = reason;

    if (reason === "workspace_not_found") {
      toast.error("Workspace not found", {
        description: "Sign in from your company URL or create an account.",
      });
      return;
    }

    if (reason === "wrong_workspace") {
      toast.error("Wrong workspace", {
        description: "Please sign in with the correct company account.",
      });
    }
  }, [searchParams]);

  const startMicrosoftSignIn = () => {
    window.location.assign(`/api/v1/auth/microsoft/authorize?next=${encodeURIComponent(APP_HOME_ROUTE)}`);
  };

  const startGoogleSignIn = () => {
    window.location.assign(`/api/v1/auth/google/authorize?next=${encodeURIComponent(APP_HOME_ROUTE)}`);
  };

  const workspaceLabel = tenant?.company_name || tenantSubdomain || "my workspace";
  const supportMailtoUrl = useMemo(() => {
    const subject = encodeURIComponent(`Password reset request - ${workspaceLabel}`);
    const body = encodeURIComponent(
      `Hello ${brand.brandName} support team,\n\n` +
        `I would like to reset the password for my account.\n\n` +
        `Workspace: ${workspaceLabel}\n` +
        `Account email: \n\n` +
        `Thank you.`
    );
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  }, [brand.brandName, workspaceLabel]);

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 gradient-ai-subtle opacity-30" />
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />

      <Navigation />

      <div className="flex flex-1 items-center justify-center p-4">
        <Card className="w-full max-w-md glass relative">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto mb-4 flex items-center justify-center">
              <img src={brand.paths.logo} alt={brand.logoAlt} className="h-16 w-auto object-contain" />
            </div>
            <CardTitle className="text-2xl font-bold">{t("auth.welcomeBack")}</CardTitle>
            <CardDescription>
              {tenant?.company_name
                ? t("auth.signInToCompany", { company: tenant.company_name })
                : t("auth.signInToAccount")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.email")}</FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="email"
                          placeholder={t("auth.emailPlaceholder")}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("auth.password")}</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="current-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button className="w-full gradient-ai" type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("auth.signingIn")}
                    </>
                  ) : (
                    t("common.signIn")
                  )}
                </Button>
              </form>
            </Form>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setForgotPasswordOpen(true)}
                className="text-sm text-primary hover:underline"
              >
                {t("auth.forgotPassword")}
              </button>
            </div>

            <div className="relative py-1">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">{t("common.or")}</span>
              </div>
            </div>

            {!rootDomain ? (
              <div className="grid gap-2">
                <Button
                  variant="outline"
                  className="w-full"
                  type="button"
                  onClick={startGoogleSignIn}
                >
                  {t("auth.signInWithGoogle")}
                </Button>
                <Button
                  variant="outline"
                  className="w-full"
                  type="button"
                  onClick={startMicrosoftSignIn}
                >
                  {t("auth.signInWithMicrosoft")}
                </Button>
              </div>
            ) : (
              <p className="text-center text-sm text-muted-foreground">
                {t("auth.useWorkspaceUrl")}
              </p>
            )}

            {!tenantSubdomain ? (
              <p className="text-center text-sm text-muted-foreground">
                {t("auth.newCompany")}{" "}
                <Link to="/register" className="text-primary hover:underline">
                  {t("auth.startFreeTrial")}
                </Link>
              </p>
            ) : (
              <p className="text-center text-sm text-muted-foreground">
                {t("auth.needAccess")}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={forgotPasswordOpen} onOpenChange={setForgotPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("auth.resetPassword.title")}</DialogTitle>
            <DialogDescription>
              {t("auth.resetPassword.description")}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-lg border bg-muted/40 px-4 py-3">
              <p className="text-sm text-muted-foreground">{t("auth.resetPassword.supportLabel")}</p>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-lg font-semibold text-primary hover:underline break-all"
              >
                {SUPPORT_EMAIL}
              </a>
            </div>
            <p className="text-sm text-muted-foreground">
              {t("auth.resetPassword.instructions")}
            </p>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setForgotPasswordOpen(false)}>
              {t("common.close")}
            </Button>
            <Button className="gradient-ai" asChild>
              <a href={supportMailtoUrl}>{t("auth.resetPassword.emailSupport")}</a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PublicFooter />
    </div>
  );
};

export default Login;
